<template>
	<div>
		<h6 class="module-title">{{ lang.nd_work_orders_employee_kpi }}</h6>

		<div v-if="loadingAttendance && !hasLoadedOnce" class="mb-3">
			<v-skeleton-loader type="table, list-item-three-line@3" />
		</div>

		<template v-else>

		<v-card flat class="section-card mb-3">
			<div class="section-head">
				<h6 class="section-title mb-0">{{ lang.nd_status || 'Status' }}</h6>
				<v-chip small outlined color="primary">{{ workOrderStatusCards.length }}</v-chip>
			</div>

			<div class="work-order-status-row">
				<v-card
					v-for="statusItem in workOrderStatusCards"
					:key="`work_order_status_${statusItem.value}`"
					flat
					class="work-order-status-card"
					:style="{ backgroundColor: statusItem.bgColor, color: statusItem.color }"
				>
					<div class="work-order-status-name">{{ statusItem.name }}</div>
					<div class="work-order-status-total">{{ statusItem.total }}</div>
				</v-card>
			</div>
		</v-card>

		<v-card flat class="section-card mb-3">
			<div class="section-head">
				<h6 class="section-title mb-0">{{ lang.nd_attendance_collections }} ({{ timeGrainLabel }})</h6>
				<v-chip small outlined color="primary">{{ attendancePeriods.length }} {{ lang.nd_periods }}</v-chip>
			</div>

			<v-data-table
				:headers="employeeAttendanceHeaders"
				:items="employeeAttendanceRows"
				dense
				hide-default-footer
				class="elevation-0 attendance-table"
			>
				<template v-slot:item="{ item, headers }">
					<tr>
						<td v-for="header in headers" :key="header.value">
							<span v-if="header.value === 'employee'">{{ item.employee }}</span>
							<v-chip
								v-else-if="header.value === 'status'"
								x-small
								dark
								:color="item.statusColor"
							>
								{{ item.statusLabel }}
							</v-chip>
							<span v-else>{{ item[header.value] }}</span>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card>

		<v-expansion-panels multiple>
			<v-expansion-panel
				v-for="employee in employeeKpiCards"
				:key="employee.id"
				class="employee-panel mb-2"
			>
				<v-expansion-panel-header class="employee-header">
					<div class="w-100">
						<b-row class="align-items-center">
							<b-col cols="12" md="5" class="mb-2 mb-md-0">
								<div class="employee-name">{{ employee.name }}</div>
								<div class="d-flex align-items-center flex-wrap gap-8 mt-1">
									<v-chip small label color="primary" outlined>
										{{ employee.branchName }}
									</v-chip>
									<v-chip
										small
										dark
										:color="statusColor(employee.empStatus)"
									>
										{{ employee.empStatusLabel }}
									</v-chip>
								</div>
							</b-col>
							<b-col cols="12" md="4" class="mb-2 mb-md-0">
								<div class="rating-label">{{ lang.nd_performance_rating }}</div>
								<v-rating
									:value="employee.starRating"
									length="5"
									half-increments
									readonly
									size="18"
									color="amber"
									empty-icon="mdi-star-outline"
								/>
							</b-col>
							<b-col cols="12" md="3">
								<div class="attendance-mini">
									<div>{{ lang.nd_working }}: {{ employee.workingHours.toFixed(1) }}h</div>
									<div>{{ lang.nd_lates }}: {{ employee.lateHours.toFixed(1) }}h</div>
								</div>
							</b-col>
						</b-row>

						<b-row class="mt-2">
							<b-col cols="6" lg="3" v-for="stat in employee.stats" :key="stat.key" class="mb-2">
								<button
									type="button"
									class="kpi-stat-btn"
									@click.stop="fetchEmployeeTasks(employee.id, stat.key)"
								>
									<div class="kpi-value">{{ stat.value }}</div>
									<div class="kpi-label">{{ stat.label }}</div>
								</button>
							</b-col>
						</b-row>
					</div>
				</v-expansion-panel-header>

				<v-expansion-panel-content>
					<b-row>
						<b-col cols="12" lg="8" class="mb-3 mb-lg-0">
							<div class="sub-title">{{ lang.nd_recent_task_snapshot }}</div>
							<div class="task-list-wrap">
								<div
									v-for="task in employee.previewTasks"
									:key="task.taskId"
									class="task-row"
								>
									<div class="task-main">
										<strong>{{ task.title }}</strong>
										<span>{{ task.customerName }} • {{ task.plateNumber }}</span>
									</div>
									<v-chip x-small :color="task.statusColor" dark>{{ task.statusLabel }}</v-chip>
								</div>
							</div>
						</b-col>

						<b-col cols="12" lg="4">
							<div class="sub-title">{{ lang.nd_admin_notes }}</div>
							<v-textarea
								v-model="notesDraft[employee.id]"
								auto-grow
								rows="2"
								dense
								outlined
								hide-details
								:placeholder="lang.nd_add_admin_note"
							/>
							<v-btn
								small
								color="primary"
								class="mt-2"
								:loading="updatingNoteId === employee.id"
								@click.stop="updateEmployeeNote(employee.id)"
							>
								{{ lang.nd_update_note }}
							</v-btn>
						</b-col>
					</b-row>
				</v-expansion-panel-content>
			</v-expansion-panel>
		</v-expansion-panels>

		<v-dialog v-model="taskDialog" max-width="900">
			<v-card>
				<v-card-title class="headline">
					{{ taskDialogTitle }}
				</v-card-title>
				<v-card-text>
					<v-data-table
						:headers="taskDialogHeaders"
						:items="taskDialogItems"
						:loading="taskDialogLoading"
						:no-data-text="lang.nd_no_tasks_found || 'No tasks found'"
						dense
						hide-default-footer
						class="elevation-0"
					/>
				</v-card-text>
				<v-card-actions class="justify-end">
					<v-btn text color="grey darken-1" @click="taskDialog = false">{{ lang.close }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		</template>
	</div>
</template>

<script>
import axios from 'axios';

export default {
	name: 'WorkOrderKpiModuleContent',
	props: {
		filters: {
			type: Object,
			required: true
		},
		branchOptions: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			loadingAttendance: false,
			hasLoadedOnce: false,
			taskDialog: false,
			taskDialogLoading: false,
			taskDialogTitle: 'Employee Tasks',
			taskDialogItems: [],
			updatingNoteId: null,
			notesDraft: {},
			employees: [],
			employeeAttendanceLogs: [],
			employeeStats: {},
			tasks: []
		};
	},
	created() {
		this.fetchWorkOrderData();
	},
	computed: {
		lang: function () {
			if (this.$cookies.get(this.$store.state.LangCooki) != null) {
				if (this.$cookies.get(this.$store.state.LangCooki) == 'en') {
					return this.$store.state.lang.en;
				} else if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') {
					return this.$store.state.lang.ur;
				} else {
					return this.$store.state.lang.ar;
				}
			}
			else if (this.$store.state.deflang == 'en') {
				return this.$store.state.lang.en;
			}
			else if (this.$store.state.deflang == 'ur') {
				return this.$store.state.lang.ur;
			} else {
				return this.$store.state.lang.ar;
			}
		},
		selectedBranchIds() {
			return this.filters.branches && this.filters.branches.length
				? this.filters.branches
				: this.branchOptions.map((branch) => branch.id);
		},
		normalizedDateRange() {
			if (!this.filters.startDate && !this.filters.endDate) {
				const end = new Date();
				const start = new Date(end);
				start.setDate(end.getDate() - 30);
				return { start, end };
			}

			const now = new Date();
			const end = this.parseISODate(this.filters.endDate) || now;
			const start =
				this.parseISODate(this.filters.startDate) ||
				new Date(end.getFullYear(), end.getMonth(), 1);
			return start <= end ? { start, end } : { start: end, end: start };
		},
		timeGrain() {
			const { start, end } = this.normalizedDateRange;
			const oneDay = 1000 * 60 * 60 * 24;
			const daysDiff = Math.floor((end - start) / oneDay);

			if (daysDiff > 31) return 'monthly';
			if (daysDiff <= 7 && this.isSameWeek(start, end)) return 'daily';
			return 'weekly';
		},
		timeGrainLabel() {
			if (this.timeGrain === 'monthly') return this.lang.nd_monthly;
			if (this.timeGrain === 'daily') return this.lang.nd_daily;
			return this.lang.nd_weekly;
		},
		attendancePeriods() {
			const { start, end } = this.normalizedDateRange;
			return this.generatePeriodsByGrain(start, end, this.timeGrain);
		},
		employeeAttendanceHeaders() {
			return [
				{ text: this.lang.nd_employee, value: 'employee', sortable: false },
				{ text: this.lang.nd_periods || 'Periods', value: 'periods', sortable: false },
				{ text: this.lang.nd_working || 'Working', value: 'attendedDays', sortable: false },
				{ text: this.lang.nd_non_attendance || 'Non-Attendance', value: 'absentDays', sortable: false },
				{ text: this.lang.nd_lates || 'Lates', value: 'lateDays', sortable: false },
				{ text: this.lang.nd_early_dismissals || 'Early Dismissals', value: 'earlyDays', sortable: false },
				{ text: this.lang.nd_working_hours || 'Avg Working Hours', value: 'avgWorkingHours', sortable: false },
				{ text: this.lang.nd_status, value: 'status', sortable: false },
				{ text: this.lang.att_reason || this.lang.notes || 'Reason', value: 'reason', sortable: false }
			];
		},
		employeeAttendanceRows() {
			const { start, end } = this.normalizedDateRange;
			return this.employees
				.filter((employee) => this.selectedBranchIds.includes(employee.branchId))
				.map((employee) => {
					const logs = this.employeeAttendanceLogs.filter((log) => {
						if (log.employeeId !== employee.id) return false;
						return this.isDateInRange(log.date, start, end);
					});

					const periods = logs.length;
					const attendedLogs = logs.filter((log) => !log.notAttended);
					const attendedDays = attendedLogs.length;
					const absentDays = logs.filter((log) => log.notAttended && log.absenceType === 'absent').length;
					const lateDays = logs.filter((log) => this.isLateAttendance(log)).length;
					const earlyDays = logs.filter((log) => this.isEarlyDismissal(log)).length;

					const totalWorkedMinutes = attendedLogs.reduce((sum, log) => {
						const startMin = this.toMinutes(log.actualArrival);
						const endMin = this.toMinutes(log.actualDeparture);
						if (startMin === null || endMin === null || endMin <= startMin) return sum;
						return sum + (endMin - startMin);
					}, 0);

					const avgWorkingHours = attendedDays > 0
						? `${(totalWorkedMinutes / 60 / attendedDays).toFixed(1)}h`
						: '0.0h';

					const status = this.attendanceRangeStatusMeta(logs, lateDays, earlyDays);
					const lastReasonLog = [...logs].reverse().find((log) => log.reason && String(log.reason).trim());
					const reason = lastReasonLog ? lastReasonLog.reason : '-';

					return {
						employee: employee.name,
						periods,
						attendedDays,
						absentDays,
						lateDays,
						earlyDays,
						avgWorkingHours,
						statusLabel: status.label,
						statusColor: status.color,
						reason
					};
				});
		},
		attendanceKpiByEmployee() {
			const grouped = {};
			this.employeeAttendanceLogs.forEach((log) => {
				const employeeId = Number(log.employeeId || 0);
				if (!employeeId) return;
				if (!grouped[employeeId]) {
					grouped[employeeId] = { workingHours: 0, lateHours: 0, nonAttendance: 0, overtime: 0 };
				}

				if (log.notAttended) {
					grouped[employeeId].nonAttendance += 1;
					return;
				}

				const arrival = this.toMinutes(log.actualArrival);
				const departure = this.toMinutes(log.actualDeparture);
				const shiftStart = this.toMinutes(log.shiftStart);
				const shiftEnd = this.toMinutes(log.shiftEnd);

				if (arrival !== null && departure !== null && departure > arrival) {
					grouped[employeeId].workingHours += (departure - arrival) / 60;
				}
				if (arrival !== null && shiftStart !== null && arrival > shiftStart) {
					grouped[employeeId].lateHours += (arrival - shiftStart) / 60;
				}
				if (departure !== null && shiftEnd !== null && departure > shiftEnd) {
					grouped[employeeId].overtime += (departure - shiftEnd) / 60;
				}
			});

			Object.keys(grouped).forEach((employeeId) => {
				grouped[employeeId] = {
					workingHours: Number(grouped[employeeId].workingHours.toFixed(1)),
					lateHours: Number(grouped[employeeId].lateHours.toFixed(1)),
					nonAttendance: grouped[employeeId].nonAttendance,
					overtime: Number(grouped[employeeId].overtime.toFixed(1))
				};
			});

			return grouped;
		},
		attendanceTotals() {
			return Object.values(this.attendanceKpiByEmployee).reduce(
				(sum, item) => {
					sum.workingHours += item.workingHours;
					sum.lateHours += item.lateHours;
					sum.nonAttendance += item.nonAttendance;
					sum.overtime += item.overtime;
					return sum;
				},
				{ workingHours: 0, lateHours: 0, nonAttendance: 0, overtime: 0 }
			);
		},
		employeeRatingsMap() {
			const ratingMap = {};
			this.employees.forEach((employee) => {
				const empId = String(employee.id);
				const stats = this.employeeStats[employee.id] || {
					totalJobOrders: 0,
					onProgress: 0,
					late: 0,
					finished: 0
				};

				const total = Number(stats.totalJobOrders || 0);
				const finished = Number(stats.finished || 0);
				const late = Number(stats.late || 0);
				const newTasks = Number(stats.onProgress || 0);

				if (total <= 0) {
					ratingMap[empId] = 0;
					return;
				}

				const finishedRatio = finished / total;
				const newRatio = newTasks / total;
				const lateRatio = late / total;

				let finalScore =
					(finishedRatio * 0.9) +
					(newRatio * 0.35) -
					(lateRatio * 0.75);

				if (!Number.isFinite(finalScore)) {
					finalScore = 0;
				}

				ratingMap[empId] = Number((Math.max(0, Math.min(1, finalScore)) * 5).toFixed(1));
			});
			return ratingMap;
		},
		employeeKpiCards() {
			return this.employees
				.filter((employee) => this.selectedBranchIds.includes(employee.branchId))
				.map((employee) => {
					const branch = this.branchOptions.find((item) => item.id === employee.branchId);
					const stats = this.employeeStats[employee.id] || {
						totalJobOrders: 0,
						onProgress: 0,
						late: 0,
						finished: 0
					};
					const attendance = this.attendanceKpiByEmployee[employee.id] || {
						workingHours: 0,
						lateHours: 0,
						nonAttendance: 0,
						overtime: 0
					};

					return {
						...employee,
						branchName: branch ? branch.name : this.lang.nd_unknown_branch,
						empStatusLabel: this.statusLabel(employee.empStatus),
						starRating: this.employeeRatingsMap[employee.id] || 0,
						workingHours: attendance.workingHours,
						lateHours: attendance.lateHours,
						stats: [
							{ key: 'all', label: this.lang.nd_total_job_orders, value: stats.totalJobOrders },
							{ key: 'on_progress', label: this.lang.nd_on_progress, value: stats.onProgress },
							{ key: 'late', label: this.lang.nd_late, value: stats.late },
							{ key: 'finished', label: this.lang.nd_finished, value: stats.finished }
						],
						previewTasks: this.tasks
							.filter((task) => task.employeeId === employee.id)
							.slice(0, 3)
							.map((task) => ({
								...task,
								statusLabel: this.taskStatusLabel(task.status),
								statusColor: this.taskStatusColor(task.status)
							}))
					};
				});
		},
		taskDialogHeaders() {
			return [
				{ text: this.lang.nd_task_id, value: 'taskId', sortable: false },
				{ text: this.lang.nd_card_id || 'Job Order', value: 'jobOrderId', sortable: false },
				{ text: this.lang.nd_task, value: 'title', sortable: false },
				{ text: this.lang.nd_customer || 'Customer', value: 'customerName', sortable: false },
				{ text: this.lang.nd_plate || 'Plate', value: 'plateNumber', sortable: false },
				{ text: this.lang.nd_status, value: 'status', sortable: false },
				{ text: this.lang.nd_due_date, value: 'dueDate', sortable: false }
			];
		},
		workOrderStatusCards() {
			const selectedEmployees = this.employees.filter((employee) => this.selectedBranchIds.includes(employee.branchId));
			const totals = selectedEmployees.reduce((sum, employee) => {
				const stats = this.employeeStats[employee.id] || {};
				sum.totalJobs += Number(stats.totalJobOrders || 0);
				sum.onProcess += Number(stats.onProgress || 0);
				sum.late += Number(stats.late || 0);
				sum.closed += Number(stats.finished || 0);
				return sum;
			}, {
				totalJobs: 0,
				onProcess: 0,
				late: 0,
				closed: 0
			});

			return [
				{
					value: 'total_jobs',
					name: this.lang.nd_total_job_orders || 'Total Jobs',
					bgColor: '#1d4ed8',
					color: '#ffffff',
					total: totals.totalJobs
				},
				{
					value: 'on_process',
					name: this.lang.nd_on_progress || 'On Process',
					bgColor: '#2563eb',
					color: '#ffffff',
					total: totals.onProcess
				},
				{
					value: 'late',
					name: this.lang.nd_late || 'Late',
					bgColor: '#dc2626',
					color: '#ffffff',
					total: totals.late
				},
				{
					value: 'closed',
					name: this.lang.nd_closed || this.lang.nd_finished || 'Closed',
					bgColor: '#16a34a',
					color: '#ffffff',
					total: totals.closed
				}
			];
		}
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.fetchWorkOrderData();
			}
		},
		employees: {
			immediate: true,
			handler(list) {
				const notes = {};
				list.forEach((employee) => {
					notes[employee.id] = this.notesDraft[employee.id] || employee.note || '';
				});
				this.notesDraft = notes;
			}
		}
	},
	methods: {
		// toISOString() converts to UTC, which moved every date one day back for users east of UTC.
		toLocalISODate(date) {
			const pad = (n) => String(n).padStart(2, '0');
			return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
		},
		dashboardApiPath() {
			const base =
				this.$store &&
				this.$store.state &&
				this.$store.state.SAMCOTEC &&
				this.$store.state.SAMCOTEC.r_path
					? this.$store.state.SAMCOTEC.r_path
					: '';
			if (base && base.indexOf('index.php') !== -1) {
				return base.replace('index.php', 'new-dashboard.php');
			}
			if (base && base.endsWith('/')) {
				return `${base}new-dashboard.php`;
			}
			return 'api/new-dashboard.php';
		},
		normalizeBranchIdsFromFilters() {
			if (this.filters && Array.isArray(this.filters.branches) && this.filters.branches.length) {
				return this.filters.branches;
			}
			return this.branchOptions.map((branch) => branch.id);
		},
		fetchWorkOrderData() {
			const post = new FormData();
			post.append('module', 'job_cards');
			post.append('startDate', this.toLocalISODate(this.normalizedDateRange.start));
			post.append('endDate', this.toLocalISODate(this.normalizedDateRange.end));
			post.append('lang', this.lang && this.lang.langname ? this.lang.langname : 'ar');

			const branchIds = this.normalizeBranchIdsFromFilters();
			branchIds.forEach((branchId, index) => {
				post.append(`branchIds[${index}]`, String(branchId));
			});

			this.loadingAttendance = true;
			axios.post(this.dashboardApiPath(), post)
				.then((response) => {
					const payload = response && response.data ? response.data : {};
					const data = payload && payload.data ? payload.data : {};

					const apiEmployees = Array.isArray(data.employeesList) ? data.employeesList : [];
					const apiAttendees = Array.isArray(data.attendeesList) ? data.attendeesList : [];
					const apiWorkStats = Array.isArray(data.employeeWorkStats) ? data.employeeWorkStats : [];
					const apiTasks = Array.isArray(data.employeeTasks) ? data.employeeTasks : [];
					const apiNotes = Array.isArray(data.notesList) ? data.notesList : [];

					if (apiEmployees.length) {
						this.employees = apiEmployees.map((row) => ({
							id: Number(row.id || 0),
							name: row.employeeName || this.lang.nd_unknown,
							branchId: Number(row.branchId || 0),
							empStatus: Number(row.status) === 1 ? 'active' : 'on_leave',
							note: ''
						})).filter((row) => row.id > 0);
					} else {
						this.employees = [];
					}

					if (apiAttendees.length) {
						this.employeeAttendanceLogs = apiAttendees.map((row) => ({
							employeeId: Number(row.employeeId || 0),
							date: row.attendanceDate || row.date || '',
							shiftStart: row.shiftStart || '08:00',
							actualArrival: row.actualArrival || '', // no punch stays empty; a '00:00' default was counted as an early dismissal
							shiftEnd: row.shiftEnd || '17:00',
							actualDeparture: row.actualDeparture || '',
							notAttended: !!row.notAttended,
							absenceType: row.absenceType || 'permission',
							justificationType: row.justificationType || 'unjustified',
							reason: row.reason || ''
						})).filter((row) => row.employeeId > 0 && row.date);
					} else {
						this.employeeAttendanceLogs = [];
					}

					if (apiWorkStats.length) {
						const mappedStats = {};
						apiWorkStats.forEach((row) => {
							const employeeId = Number(row.employeeId || 0);
							if (!employeeId) return;
							mappedStats[employeeId] = {
								totalJobOrders: Number(row.totalJobOrders || 0),
								onProgress: Number(row.onProgress || 0),
								late: Number(row.late || 0),
								finished: Number(row.finished || 0)
							};
						});
						this.employeeStats = mappedStats;
					} else {
						this.employeeStats = {};
					}

					if (apiTasks.length) {
						this.tasks = apiTasks.map((row) => ({
							taskId: row.taskId || `TASK-${row.workId || row.rowId || 0}`,
							employeeId: Number(row.employeeId || 0),
							title: row.itemName || '-',
							status: row.status || 'on_progress',
							dueDate: row.dueDate || '',
							jobOrderId: Number(row.jobOrderId || 0),
							customerName: row.customerName || '-',
							plateNumber: row.plateNumber || '-'
						})).filter((row) => row.employeeId > 0);
					} else {
						this.tasks = [];
					}

					if (apiNotes.length) {
						const notesMap = {};
						apiNotes.forEach((row) => {
							const employeeId = Number(row.employeeId || 0);
							if (!employeeId) return;
							notesMap[employeeId] = row.note || '';
						});
						this.notesDraft = {
							...this.notesDraft,
							...notesMap
						};
					}
				})
				.catch(() => {
					this.employees = [];
					this.employeeAttendanceLogs = [];
					this.employeeStats = {};
					this.tasks = [];
				})
				.finally(() => {
					this.loadingAttendance = false;
					this.hasLoadedOnce = true;
				});
		},
		parseISODate(value) {
			if (!value) return null;
			const [year, month, day] = value.split('-').map(Number);
			if (!year || !month || !day) return null;
			return new Date(year, month - 1, day);
		},
		isSameWeek(start, end) {
			const startOfWeek = (date) => {
				const copy = new Date(date);
				const day = copy.getDay();
				const diff = day === 0 ? -6 : 1 - day;
				copy.setDate(copy.getDate() + diff);
				copy.setHours(0, 0, 0, 0);
				return copy;
			};
			return startOfWeek(start).getTime() === startOfWeek(end).getTime();
		},
		generatePeriodsByGrain(start, end, grain) {
			const periods = [];
			let cursor = new Date(start);

			if (grain === 'monthly') {
				cursor = new Date(start.getFullYear(), start.getMonth(), 1);
				while (cursor <= end) {
					periods.push({
						key: `period_${periods.length}`,
						label: cursor.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
					});
					cursor.setMonth(cursor.getMonth() + 1);
				}
				return periods;
			}

			if (grain === 'weekly') {
				while (cursor <= end) {
					const weekEnd = new Date(cursor);
					weekEnd.setDate(weekEnd.getDate() + 6);
					if (weekEnd > end) weekEnd.setTime(end.getTime());

					periods.push({
						key: `period_${periods.length}`,
						label: `W${periods.length + 1} (${cursor.toLocaleDateString('en-US', {
							month: '2-digit',
							day: '2-digit'
						})} - ${weekEnd.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })})`
					});
					cursor.setDate(cursor.getDate() + 7);
				}
				return periods;
			}

			while (cursor <= end) {
				periods.push({
					key: `period_${periods.length}`,
					label: cursor.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
				});
				cursor.setDate(cursor.getDate() + 1);
			}
			return periods;
		},
		formatMetric(key, value) {
			if (key === 'workingHours' || key === 'lateHours' || key === 'overtime') return `${value}h`;
			return `${value}`;
		},
		isDateInRange(dateValue, start, end) {
			const date = this.parseISODate(dateValue);
			if (!date) return false;
			return date >= start && date <= end;
		},
		toMinutes(timeValue) {
			if (!timeValue || String(timeValue).indexOf(':') === -1) return null;
			const parts = String(timeValue).split(':').map(Number);
			if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return null;
			return parts[0] * 60 + parts[1];
		},
		isLateAttendance(attendance) {
			if (attendance.notAttended) return false;
			const shift = this.toMinutes(attendance.shiftStart);
			const arrival = this.toMinutes(attendance.actualArrival);
			if (shift === null || arrival === null) return false;
			return arrival > shift;
		},
		isEarlyDismissal(attendance) {
			if (attendance.notAttended) return false;
			const shiftEnd = this.toMinutes(attendance.shiftEnd);
			const departure = this.toMinutes(attendance.actualDeparture);
			if (shiftEnd === null || departure === null) return false;
			return departure < shiftEnd;
		},
		attendanceStatusMeta(attendance) {
			if (attendance.notAttended) {
				if (attendance.absenceType === 'vacation') {
					return { color: 'purple', label: this.lang.att_on_vacation || 'On Vacation' };
				}
				if (attendance.absenceType === 'permission') {
					return { color: 'orange', label: this.lang.att_permission || 'Permission' };
				}
				return { color: 'red', label: this.lang.att_absent || 'Absent' };
			}

			const late = this.isLateAttendance(attendance);
			const early = this.isEarlyDismissal(attendance);
			if (!late && !early) {
				return { color: 'green', label: this.lang.att_present_on_time || 'Present & On Time' };
			}

			if (attendance.justificationType === 'justified_delay' || attendance.justificationType === 'justified_departure') {
				return { color: 'orange', label: this.lang.att_late_justified || 'Late (Justified)' };
			}

			return { color: 'red', label: this.lang.att_late_early_unjustified || 'Late / Early (Unjustified)' };
		},
		attendanceRangeStatusMeta(logs, lateDays, earlyDays) {
			if (!logs || logs.length === 0) {
				return { color: 'grey', label: this.lang.nd_unknown || 'Unknown' };
			}

			const attended = logs.filter((log) => !log.notAttended).length;
			if (attended === 0) {
				const allVacation = logs.every((log) => log.absenceType === 'vacation');
				if (allVacation) return { color: 'purple', label: this.lang.att_on_vacation || 'On Vacation' };
				return { color: 'red', label: this.lang.att_absent || 'Absent' };
			}

			if (lateDays === 0 && earlyDays === 0) {
				return { color: 'green', label: this.lang.att_present_on_time || 'Present & On Time' };
			}

			const hasUnjustified = logs.some(
				(log) => !log.notAttended && (this.isLateAttendance(log) || this.isEarlyDismissal(log)) && log.justificationType === 'unjustified'
			);
			if (!hasUnjustified) {
				return { color: 'orange', label: this.lang.att_late_justified || 'Late (Justified)' };
			}

			return { color: 'red', label: this.lang.att_late_early_unjustified || 'Late / Early (Unjustified)' };
		},
		statusLabel(status) {
			const map = {
				active: this.lang.nd_active,
				on_break: this.lang.nd_on_break,
				on_leave: this.lang.nd_on_leave
			};
			return map[status] || this.lang.nd_unknown;
		},
		statusColor(status) {
			const map = {
				active: 'green darken-2',
				on_break: 'orange darken-2',
				on_leave: 'blue-grey'
			};
			return map[status] || 'grey';
		},
		taskStatusLabel(status) {
			const map = {
				all: this.lang.nd_all,
				on_progress: this.lang.nd_on_progress,
				late: this.lang.nd_late,
				finished: this.lang.nd_finished
			};
			return map[status] || this.lang.nd_unknown;
		},
		taskStatusColor(status) {
			const map = {
				on_progress: 'blue',
				late: 'red darken-2',
				finished: 'green darken-2'
			};
			return map[status] || 'grey';
		},
		fetchEmployeeTasks(employeeId, status) {
			const employee = this.employees.find((item) => item.id === employeeId);
			const statusLabel = this.taskStatusLabel(status);
			this.taskDialogTitle = `${employee ? employee.name : this.lang.nd_employee} - ${statusLabel} ${this.lang.nd_tasks}`;
			this.taskDialogItems = [];
			this.taskDialog = true;
			this.taskDialogLoading = true;

			const post = new FormData();
			post.append('module', 'work_order_employee_tasks');
			post.append('employeeId', String(employeeId));
			post.append('status', String(status || 'all'));
			post.append('startDate', this.toLocalISODate(this.normalizedDateRange.start));
			post.append('endDate', this.toLocalISODate(this.normalizedDateRange.end));
			post.append('lang', this.lang && this.lang.langname ? this.lang.langname : 'ar');

			const branchIds = this.normalizeBranchIdsFromFilters();
			branchIds.forEach((branchId, index) => {
				post.append(`branchIds[${index}]`, String(branchId));
			});

			axios.post(this.dashboardApiPath(), post)
				.then((response) => {
					const payload = response && response.data ? response.data : {};
					const rows = payload && payload.data && Array.isArray(payload.data.tasks)
						? payload.data.tasks
						: [];

					this.taskDialogItems = rows.map((task) => ({
						taskId: task.taskId,
						jobOrderId: task.jobOrderId || 0,
						title: task.itemName || '-',
						customerName: task.customerName || '-',
						plateNumber: task.plateNumber || '-',
						status: this.taskStatusLabel(task.status),
						dueDate: task.dueDate || ''
					}));
				})
				.catch(() => {
					const filtered = this.tasks.filter((task) => {
						if (task.employeeId !== employeeId) return false;
						if (status === 'all') return true;
						return task.status === status;
					});

					this.taskDialogItems = filtered.map((task) => ({
						taskId: task.taskId,
						jobOrderId: task.jobOrderId || 0,
						title: task.title,
						customerName: task.customerName || '-',
						plateNumber: task.plateNumber || '-',
						status: this.taskStatusLabel(task.status),
						dueDate: task.dueDate
					}));
				})
				.finally(() => {
					this.taskDialogLoading = false;
				});
		},
		updateEmployeeNote(employeeId) {
			if (this.updatingNoteId !== null) {
				return;
			}
			this.updatingNoteId = employeeId;
			const employee = this.employees.find((item) => item.id === employeeId);
			const branchId = employee ? Number(employee.branchId || 0) : 0;
			const noteDate = this.filters && this.filters.endDate ? this.filters.endDate : this.toLocalISODate(new Date());
			const note = this.notesDraft[employeeId] || '';

			const post = new FormData();
			post.append('module', 'work_order_note_save');
			post.append('employeeId', String(employeeId));
			post.append('branchId', String(branchId));
			post.append('noteDate', noteDate);
			post.append('note', note);

			axios.post(this.dashboardApiPath(), post)
				.then((response) => {
					const payload = response && response.data ? response.data : {};
					const ok = Number(payload.status || 0) === 1;
					const msg = payload.msg || (ok ? (this.lang.saved || 'Saved') : (this.lang.server_not_responding || 'Server not responding'));

					if (!ok) {
						if (this.$store && this.$store.commit) {
							this.$store.commit('setSnackbar', {
								show: true,
								text: msg,
								color: 'warning'
							});
						}
						return;
					}

					if (employee) {
						employee.note = note;
					}
					if (this.$store && this.$store.commit) {
						this.$store.commit('setSnackbar', {
							show: true,
							text: msg,
							color: 'success'
						});
					}
				})
				.catch(() => {
					if (this.$store && this.$store.commit) {
						this.$store.commit('setSnackbar', {
							show: true,
							text: this.lang.server_not_responding || 'Server not responding',
							color: 'warning'
						});
					}
				})
				.finally(() => {
					this.updatingNoteId = null;
				});
		}
	}
};
</script>

<style scoped>
.module-title {
	margin-bottom: 8px;
	font-size: 1rem;
	font-weight: 700;
	color: #2d3748;
}

.section-card {
	border: 1px solid #e6ecf3;
	border-radius: 10px;
	padding: 14px;
	background: #ffffff;
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	margin-bottom: 12px;
}

.section-title {
	font-size: 0.95rem;
	font-weight: 700;
	color: #2d3748;
}

.work-order-status-row {
	display: flex;
	flex-wrap: nowrap;
	gap: 10px;
	overflow-x: auto;
	padding-bottom: 2px;
}

.work-order-status-card {
	flex: 1 1 160px;
	min-width: 160px;
	border-radius: 10px;
	padding: 10px 12px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-weight: 700;
}

.work-order-status-name {
	font-size: 0.82rem;
	opacity: 0.95;
}

.work-order-status-total {
	font-size: 1.1rem;
	line-height: 1;
}

.attendance-table {
	border: 1px solid #edf1f6;
	border-radius: 8px;
}

.attendance-table ::v-deep th {
	background: #f8fafc;
	font-size: 0.8rem;
	font-weight: 700;
	color: #4a5568;
}

.employee-panel {
	border: 1px solid #dce4ef;
	border-radius: 10px;
}

.employee-header {
	padding: 8px 4px;
}

.employee-name {
	font-size: 0.95rem;
	font-weight: 700;
	color: #1f2937;
}

.gap-8 {
	gap: 8px;
}

.rating-label {
	font-size: 0.76rem;
	color: #64748b;
	font-weight: 600;
}

.attendance-mini {
	font-size: 0.76rem;
	color: #475569;
	font-weight: 600;
	line-height: 1.45;
}

.kpi-stat-btn {
	width: 100%;
	border: 1px solid #dbe3ef;
	background: #f8fbff;
	border-radius: 8px;
	padding: 8px 6px;
	text-align: left;
	transition: all 0.2s ease;
}

.kpi-stat-btn:hover {
	border-color: #93c5fd;
	background: #eef6ff;
}

.kpi-value {
	font-size: 1rem;
	font-weight: 800;
	color: #1e3a8a;
	line-height: 1.2;
}

.kpi-label {
	font-size: 0.72rem;
	font-weight: 600;
	color: #475569;
}

.sub-title {
	font-size: 0.82rem;
	font-weight: 700;
	color: #334155;
	margin-bottom: 8px;
}

.task-list-wrap {
	border: 1px solid #e6ecf3;
	border-radius: 8px;
	background: #fcfdff;
}

.task-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 10px;
	border-bottom: 1px solid #edf2f7;
}

.task-row:last-child {
	border-bottom: 0;
}

.task-main {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.task-main strong {
	font-size: 0.8rem;
	color: #1f2937;
}

.task-main span {
	font-size: 0.72rem;
	color: #64748b;
}

@media (max-width: 767px) {
	.employee-header {
		padding: 4px 0;
	}
}
</style>
